import React, { useState, useEffect } from "react";
import { Button, Modal, Form, Alert, Row, Col, Card, Badge, Spinner, Tab, Tabs } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import { apiGet, apiPost, apiPut, apiDelete } from "../services/BaseService";
import ProgramData from "../models/ProgramData";
import { IMAGE_BASE } from "../Config";
import TicketOptionsManager from "../components/TicketOptionsManager";

const ManageAllPlaysPage: React.FC = () => {
  const [plays, setPlays] = useState<ProgramData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingPlay, setEditingPlay] = useState<ProgramData | null>(null);
  const [selectedPlayForSchedule, setSelectedPlayForSchedule] = useState<ProgramData | null>(null);
  const [showTicketManager, setShowTicketManager] = useState(false);
  const [selectedScheduleForTickets, setSelectedScheduleForTickets] = useState<any>(null);
  const [ticketTypes, setTicketTypes] = useState([{ ticketName: '', price: 0 }]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPlays, setFilteredPlays] = useState<ProgramData[]>([]);
  const [displayCount, setDisplayCount] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Core program data
  const [formData, setFormData] = useState({
    title: "",
    programCode: "",
    description: "",
    programType: "",
    language: "",
    durationHours: 0,
    durationMinutes: 0,
    email: "",
    phone: "",
    image1: "",
    image2: "",
    image3: "",
    isFeatured: false
  });

  // Related data
  const [programTypes, setProgramTypes] = useState<any[]>([]);
  const [genres, setGenres] = useState<any[]>([]);
  const [memberTypes, setMemberTypes] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  
  // Language options
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'te', name: 'Telugu' },
    { code: 'mr', name: 'Marathi' },
    { code: 'ta', name: 'Tamil' },
    { code: 'gu', name: 'Gujarati' },
    { code: 'kn', name: 'Kannada' },
    { code: 'ml', name: 'Malayalam' },
    { code: 'pa', name: 'Punjabi' },
    { code: 'or', name: 'Odia' },
    { code: 'as', name: 'Assamese' }
  ];
  
  // Selected relationships
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  
  // Group search filters
  const [groupSearchName, setGroupSearchName] = useState("");
  const [groupSearchCountry, setGroupSearchCountry] = useState("");
  const [groupSearchState, setGroupSearchState] = useState("");
  const [filteredGroups, setFilteredGroups] = useState<any[]>([]);

  // Schedule form
  const [scheduleData, setScheduleData] = useState({
    startDate: "",
    endDate: "",
    timing: "",
    address: "",
    pinCode: "",
    state: "",
    country: "IN",
    organizerName: "",
    organizerPhone: ""
  });

  // File uploads
  const [selectedFiles, setSelectedFiles] = useState<{
    image1: File | null;
    image2: File | null;
    image3: File | null;
  }>({
    image1: null,
    image2: null,
    image3: null
  });

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchPlaysData(1, 12, true);
    fetchProgramTypes();
    fetchGenres();
    fetchMemberTypes();
    fetchGroups();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (searchQuery !== undefined || groupSearchCountry !== undefined || groupSearchState !== undefined || selectedGroups !== undefined) {
      setCurrentPage(1);
      fetchPlaysData(1, 12, true);
    }
  }, [searchQuery, groupSearchCountry, groupSearchState, selectedGroups]);



  useEffect(() => {
    filterGroups();
  }, [groups, groupSearchName, groupSearchCountry, groupSearchState]);

  const filterGroups = () => {
    let filtered = groups;
    
    if (groupSearchName) {
      filtered = filtered.filter(group => 
        group.name.toLowerCase().includes(groupSearchName.toLowerCase())
      );
    }
    
    if (groupSearchCountry) {
      filtered = filtered.filter(group => group.country === groupSearchCountry);
    }
    
    if (groupSearchState) {
      filtered = filtered.filter(group => group.state === groupSearchState);
    }
    
    setFilteredGroups(filtered);
  };

  const handleGroupCountryChange = (country: string) => {
    setGroupSearchCountry(country);
    setGroupSearchState("");
    setSelectedGroups([]);
    if (country) {
      fetchStates(country);
      fetchGroups();
    } else {
      setStates([]);
      setGroups([]);
    }
  };

  const handleGroupStateChange = (state: string) => {
    setGroupSearchState(state);
    setSelectedGroups([]);
    fetchGroups();
  };

  const fetchPlaysData = async (page = 1, limit = 12, reset = false) => {
    try {
      setLoading(true);
      
      // Use include-types endpoint to get only plays
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        includeTypes: '6839c953077dbaaa4e2581da' // Theater Play type ID
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (groupSearchCountry) params.append('country', groupSearchCountry);
      if (groupSearchState) params.append('state', groupSearchState);
      if (selectedGroups[0]) params.append('groupId', selectedGroups[0]);
      
      const response = await apiGet(`/programs/include-types?${params.toString()}`);
      const playsData = response.programs || [];
      
      if (reset || page === 1) {
        setPlays(playsData);
        setFilteredPlays(playsData);
        setDisplayCount(playsData.length);
      } else {
        setPlays(prev => [...prev, ...playsData]);
        setFilteredPlays(prev => [...prev, ...playsData]);
        setDisplayCount(prev => prev + playsData.length);
      }
      
      setHasMore(playsData.length === limit);
    } catch (error) {
      setError("Failed to fetch plays");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const fetchProgramTypes = async () => {
    try {
      const data = await apiGet('/programtypes');
      setProgramTypes(data);
    } catch (error) {
      console.error('Failed to fetch program types:', error);
      // Set default Theater Play type if API fails
      setProgramTypes([{ _id: 'default', name: 'Theater Play' }]);
    }
  };

  const fetchGenres = async () => {
    try {
      const data = await apiGet('/genres');
      setGenres(data);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
      // Set default genres if API fails
      setGenres([
        { _id: '1', name: 'Drama' },
        { _id: '2', name: 'Comedy' },
        { _id: '3', name: 'Musical' },
        { _id: '4', name: 'Tragedy' }
      ]);
    }
  };

  const fetchMemberTypes = async () => {
    try {
      const data = await apiGet('/membertypes');
      setMemberTypes(data);
    } catch (error) {
      console.error('Failed to fetch member types:', error);
      // Set default member types if API fails
      setMemberTypes([
        { _id: '1', name: 'Director' },
        { _id: '2', name: 'Actor' },
        { _id: '3', name: 'Writer' },
        { _id: '4', name: 'Producer' },
        { _id: '5', name: 'Music Director' },
        { _id: '6', name: 'Choreographer' }
      ]);
    }
  };

  const fetchGroups = async () => {
    try {
      let url = '/groups';
      const params = new URLSearchParams();
      if (groupSearchCountry) params.append('country', groupSearchCountry);
      if (groupSearchState) params.append('state', groupSearchState);
      
      if (params.toString()) {
        url = `/groups/filter?${params.toString()}`;
      }
      
      const data = await apiGet(url);
      setGroups(data.groups || data);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const data = await apiGet('/groups/distinct/countries');
      setCountries(data.map((code: string) => ({ countryCode: code, name: getCountryName(code) })));
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryCode: string) => {
    try {
      const data = await apiGet(`/groups/distinct/states/${countryCode}`);
      setStates(data.map((code: string) => ({ stateCode: code, name: getStateName(code) })));
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const getCountryName = (code: string) => {
    const countryMap: { [key: string]: string } = {
      "IN": "India",
      "US": "United States",
      "UK": "United Kingdom",
      "CA": "Canada",
      "AU": "Australia"
    };
    return countryMap[code] || code;
  };

  const getStateName = (code: string) => {
    const stateMap: { [key: string]: string } = {
      "MH": "Maharashtra",
      "KA": "Karnataka",
      "TN": "Tamil Nadu",
      "DL": "Delhi",
      "GJ": "Gujarat",
      "RJ": "Rajasthan",
      "UP": "Uttar Pradesh",
      "WB": "West Bengal",
      "AP": "Andhra Pradesh",
      "TS": "Telangana",
      "KL": "Kerala",
      "OR": "Odisha",
      "PB": "Punjab",
      "HR": "Haryana",
      "AS": "Assam",
      "BR": "Bihar",
      "CG": "Chhattisgarh",
      "GA": "Goa",
      "HP": "Himachal Pradesh",
      "JH": "Jharkhand",
      "MP": "Madhya Pradesh",
      "MN": "Manipur",
      "ML": "Meghalaya",
      "MZ": "Mizoram",
      "NL": "Nagaland",
      "SK": "Sikkim",
      "TR": "Tripura",
      "UK": "Uttarakhand"
    };
    return stateMap[code] || code;
  };

  const generateProgramCode = () => {
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PLAY-${timestamp}-${random}`;
  };

  const fetchProgramGenres = async (programId: string) => {
    try {
      const data = await apiGet(`/program-genre/program/${programId}`);
      const genreIds = data.map((pg: any) => pg.genreId);
      setSelectedGenres(genreIds);
    } catch (error) {
      setSelectedGenres([]);
      console.error('Failed to fetch program genres:', error);
    }
  };

  const fetchProgramGroups = async (programId: string) => {
    try {
      const data = await apiGet(`/programgroups/program/${programId}`);
      const groupIds = data.map((pg: any) => pg.groupId);
      setSelectedGroups(groupIds);
    } catch (error) {
      setSelectedGroups([]);
      console.error('Failed to fetch program groups:', error);
    }
  };

  const fetchProgramMembers = async (programId: string) => {
    try {
      const data = await apiGet(`/programmembers/program/${programId}`);
      const memberData = data.map((member: any) => ({
        firstName: member.firstName || '',
        lastName: member.lastName || '',
        memberTypeId: member.memberTypeId?._id || member.memberTypeId || '',
        userId: member.userId?._id || member.userId || ''
      }));
      setMembers(memberData);
    } catch (error) {
      console.error('Failed to fetch program members:', error);
    }
  };

  const handleShowModal = (play?: ProgramData) => {
    if (play) {
      setEditingPlay(play);
      setFormData({
        title: play.title || "",
        programCode: play.programCode || "",
        description: play.description || "",
        programType: play.programType || "",
        language: play.language || "",
        durationHours: play.durationHours || 0,
        durationMinutes: play.durationMinutes || 0,
        email: play.email || "",
        phone: play.phone || "",
        image1: play.image1 || "",
        image2: play.image2 || "",
        image3: play.image3 || "",
        isFeatured: play.isFeatured || false
      });
      // Fetch existing relationships for editing
      fetchProgramGenres(play._id);
      fetchProgramGroups(play._id);
      fetchProgramMembers(play._id);
    } else {
      setEditingPlay(null);
      setFormData({
        title: "",
        programCode: generateProgramCode(),
        description: "",
        programType: "6839c953077dbaaa4e2581da", // Default to Theater Play
        language: "",
        durationHours: 0,
        durationMinutes: 0,
        email: "",
        phone: "",
        image1: "",
        image2: "",
        image3: "",
        isFeatured: false
      });
      setSelectedGenres([]);
      setSelectedGroups([]);
      setMembers([]);
    }
    setSelectedFiles({ image1: null, image2: null, image3: null });
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleShowScheduleModal = (play: ProgramData) => {
    setSelectedPlayForSchedule(play);
    setScheduleData({
      startDate: "",
      endDate: "",
      timing: "",
      address: "",
      pinCode: "",
      state: "",
      country: "",
      organizerName: "",
      organizerPhone: ""
    });
    fetchSchedules(play._id);
    setStates([]);
    setShowScheduleModal(true);
  };

  const fetchSchedules = async (programId: string) => {
    try {
      const data = await apiGet(`/programschedules/program/${programId}`);
      setSchedules(data);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleScheduleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setScheduleData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'country' && value) {
      fetchStates(value);
      setScheduleData(prev => ({ ...prev, state: '' }));
    }
  };

  const handleFileChange = (imageKey: 'image1' | 'image2' | 'image3') => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFiles(prev => ({ ...prev, [imageKey]: e.target.files![0] }));
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!file) return null;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const result = await apiPost('/image/upload/theaterPlayImage', formData);
      return `/theaterPlayImage/${result.fileName}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const addMember = () => {
    setMembers(prev => [...prev, {
      firstName: "",
      lastName: "",
      memberTypeId: "",
      userId: ""
    }]);
  };

  const updateMember = (index: number, field: string, value: string) => {
    setMembers(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));
  };

  const removeMember = (index: number) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const submitData = {
        ...scheduleData,
        programId: selectedPlayForSchedule?._id
      };

      await apiPost('/programschedules', submitData);
      setSuccess("Schedule created successfully");
      fetchSchedules(selectedPlayForSchedule!._id);
      setScheduleData({
        startDate: "",
        endDate: "",
        timing: "",
        address: "",
        pinCode: "",
        state: "",
        country: "",
        organizerName: "",
        organizerPhone: ""
      });
    } catch (error) {
      setError("Failed to create schedule");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setUploading(true);
      
      // Upload images
      const imageUploads = await Promise.all([
        selectedFiles.image1 ? uploadImage(selectedFiles.image1) : formData.image1,
        selectedFiles.image2 ? uploadImage(selectedFiles.image2) : formData.image2,
        selectedFiles.image3 ? uploadImage(selectedFiles.image3) : formData.image3
      ]);

      const submitData = {
        ...formData,
        image1: imageUploads[0] || "",
        image2: imageUploads[1] || "",
        image3: imageUploads[2] || ""
      };

      // Create/Update Program
      const savedProgram = editingPlay 
        ? await apiPut(`/programs/${editingPlay._id}`, submitData)
        : await apiPost('/programs', submitData);
      
      const programId = savedProgram._id;

      // Save Program Genres
      if (selectedGenres.length > 0) {
        try {
          await Promise.all(selectedGenres.map(genreId => 
            apiPost('/program-genre', { programId, genreId })
          ));
        } catch (error) {
          console.error('Failed to save program genres:', error);
        }
      }

      // Save Program Groups
      if (selectedGroups.length > 0) {
        try {
          await Promise.all(selectedGroups.map(groupId => 
            apiPost('/programgroups', { programId, groupId })
          ));
        } catch (error) {
          console.error('Failed to save program groups:', error);
        }
      }

      // Save Program Members
      if (members.length > 0) {
        try {
          await Promise.all(members.map(member => 
            apiPost('/programmembers', { ...member, programId })
          ));
        } catch (error) {
          console.error('Failed to save program members:', error);
        }
      }

      setSuccess(editingPlay ? "Play updated successfully" : "Play created successfully");
      await fetchPlaysData(1, 12, true);
      setShowModal(false);
    } catch (error) {
      setError(editingPlay ? "Failed to update play" : "Failed to create play");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (playId: string) => {
    if (window.confirm("Are you sure you want to delete this play?")) {
      try {
        await apiDelete(`/programs/${playId}`);
        setSuccess("Play deleted successfully");
        await fetchPlaysData(1, 12, true);
      } catch (error) {
        setError("Failed to delete play");
      }
    }
  };

  const addTicketType = () => {
    setTicketTypes([...ticketTypes, { ticketName: '', price: 0 }]);
  };

  const removeTicketType = (index: number) => {
    if (ticketTypes.length > 1) {
      setTicketTypes(ticketTypes.filter((_, i) => i !== index));
    }
  };

  const updateTicketType = (index: number, field: 'ticketName' | 'price', value: string | number) => {
    const updated = [...ticketTypes];
    updated[index] = { ...updated[index], [field]: value };
    setTicketTypes(updated);
  };

  const loadTicketOptionsForSchedule = async (scheduleId: string) => {
    try {
      setLoading(true);
      const options = await apiGet(`/tickets/schedule/${scheduleId}`);
      if (options.length > 0) {
        setTicketTypes(options.map((opt: any) => ({
          ticketName: opt.ticketName,
          price: opt.price
        })));
      } else {
        setTicketTypes([{ ticketName: '', price: 0 }]);
      }
    } catch (err) {
      setTicketTypes([{ ticketName: '', price: 0 }]);
    } finally {
      setLoading(false);
    }
  };

  const saveTicketTypes = async () => {
    if (!selectedScheduleId) {
      setError("Please select a schedule first");
      return;
    }
    
    const validOptions = ticketTypes.filter(opt => opt.ticketName.trim() && opt.price > 0);
    
    if (validOptions.length === 0) {
      setError("Please add at least one valid ticket option");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiPost(`/tickets/schedule/${selectedScheduleId}`, { ticketOptions: validOptions });
      setSuccess("Ticket options saved successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Failed to save ticket options");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      try {
        await apiDelete(`/programschedules/${scheduleId}`);
        setSuccess("Schedule deleted successfully");
        fetchSchedules(selectedPlayForSchedule!._id);
      } catch (error) {
        setError("Failed to delete schedule");
      }
    }
  };

  const formatDuration = (hours?: number, minutes?: number) => {
    if (!hours && !minutes) return "Duration TBA";
    const h = hours ? `${hours}h` : "";
    const m = minutes ? `${minutes}m` : "";
    return `${h} ${m}`.trim();
  };

  return (
    <PermissionWrapper pageName="ManageAllPlaysPage">
      <div>
        <Row className="mb-4">
          <Col>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <h3 className="mb-3 mb-md-0">
                <i className="bi bi-stars me-2"></i>
                Manage All Plays
              </h3>
              <Button variant="primary" onClick={() => handleShowModal()}>
                <i className="bi bi-plus-circle me-2"></i>
                Add Play
              </Button>
            </div>
          </Col>
        </Row>

        {/* Search and Filter Section */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Search & Filter</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={3}>
                <Form.Select value={groupSearchCountry} onChange={(e) => handleGroupCountryChange(e.target.value)}>
                  <option value="">All Countries</option>
                  {countries.map(country => (
                    <option key={country.countryCode} value={country.countryCode}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select value={groupSearchState} onChange={(e) => handleGroupStateChange(e.target.value)} disabled={!groupSearchCountry}>
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select value={selectedGroups[0] || ""} onChange={(e) => {
                  const groupId = e.target.value;
                  setSelectedGroups(groupId ? [groupId] : []);
                }}>
                  <option value="">All Groups</option>
                  {filteredGroups.map(group => (
                    <option key={group._id} value={group._id}>
                      {group.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Search plays..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="me-2"
                  />
                  <Button variant="outline-secondary" onClick={() => handleSearch(searchQuery)}>
                    <i className="bi bi-search"></i>
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <small className="text-muted">
            {filteredPlays.length > 0 ? `Showing ${filteredPlays.length} plays` : "No plays found"}
            {searchQuery && ` for "${searchQuery}"`}
          </small>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Row className="g-3">
              {filteredPlays.map((play) => (
                <Col xs={12} sm={6} md={4} lg={3} key={play._id}>
                  <Card className="h-100 play-card">
                    <div className="play-image-container">
                      {play.image1 ? (
                        <Card.Img 
                          variant="top" 
                          src={IMAGE_BASE + play.image1}
                          className="play-image"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/default-profile-image.jpg';
                          }}
                        />
                      ) : (
                        <div className="play-image-placeholder">
                          <i className="bi bi-play-circle display-4 text-muted"></i>
                        </div>
                      )}
                    </div>
                    <Card.Body className="d-flex flex-column">
                      <Card.Title className="h6">{play.title}</Card.Title>
                      <Card.Text className="small text-muted flex-grow-1">
                        {play.description ? 
                          (play.description.length > 80 ? 
                            play.description.substring(0, 80) + '...' : 
                            play.description
                          ) : 'No description available'
                        }
                      </Card.Text>
                      <div className="play-info mb-2">
                        <div className="small text-muted">
                          <i className="bi bi-clock me-1"></i>
                          {formatDuration(play.durationHours, play.durationMinutes)}
                        </div>
                        {play.language && (
                          <div className="small text-muted">
                            <i className="bi bi-translate me-1"></i>
                            {play.language}
                          </div>
                        )}
                      </div>
                      <div className="d-flex gap-1 mt-auto">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleShowModal(play)}
                          className="flex-fill"
                        >
                          <i className="bi bi-pencil me-1"></i>
                          Edit
                        </Button>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => handleShowScheduleModal(play)}
                          title="Manage Schedules"
                        >
                          <i className="bi bi-calendar"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(play._id)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            
            {hasMore && (
              <div className="text-center mt-4">
                <Button 
                  variant="outline-primary" 
                  onClick={() => {
                    const nextPage = currentPage + 1;
                    setCurrentPage(nextPage);
                    fetchPlaysData(nextPage, 12, false);
                  }}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </>
        )}

        {/* Play Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{editingPlay ? "Edit Play" : "Add Play"}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Tabs defaultActiveKey="basic" className="mb-3">
                <Tab eventKey="basic" title="Basic Info">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Play Title</Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Program Code</Form.Label>
                        <div className="d-flex">
                          <Form.Control
                            type="text"
                            name="programCode"
                            value={formData.programCode}
                            onChange={handleInputChange}
                            required
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => setFormData(prev => ({ ...prev, programCode: generateProgramCode() }))}
                            className="ms-2"
                            type="button"
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Language</Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Language</option>
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {lang.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Hours</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationHours"
                          value={formData.durationHours}
                          onChange={handleInputChange}
                          min="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Duration Minutes</Form.Label>
                        <Form.Control
                          type="number"
                          name="durationMinutes"
                          value={formData.durationMinutes}
                          onChange={handleInputChange}
                          min="0"
                          max="59"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </Form.Group>

                  {editingPlay && (
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="featured-switch"
                        label="Featured Play"
                        checked={formData.isFeatured}
                        onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                      />
                      <Form.Text className="text-muted">
                        Featured plays appear prominently on the homepage
                      </Form.Text>
                    </Form.Group>
                  )}
                </Tab>

                <Tab eventKey="images" title="Images">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 1</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image1')}
                        />
                        {formData.image1 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image1}
                              alt="Image 1"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 2</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image2')}
                        />
                        {formData.image2 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image2}
                              alt="Image 2"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Image 3</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange('image3')}
                        />
                        {formData.image3 && (
                          <div className="mt-2">
                            <img 
                              src={IMAGE_BASE + formData.image3}
                              alt="Image 3"
                              style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                              className="rounded"
                            />
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                </Tab>

                <Tab eventKey="genres" title="Genres">
                  <Form.Group className="mb-3">
                    <Form.Label>Select Genres</Form.Label>
                    <div className="row">
                      {genres.map(genre => (
                        <div key={genre._id} className="col-md-4 col-sm-6">
                          <Form.Check
                            type="checkbox"
                            id={`genre-${genre._id}`}
                            label={genre.name}
                            checked={selectedGenres.includes(genre._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedGenres(prev => [...prev, genre._id]);
                              } else {
                                setSelectedGenres(prev => prev.filter(id => id !== genre._id));
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {genres.length === 0 && (
                      <p className="text-muted">No genres available. Please add genres first.</p>
                    )}
                  </Form.Group>
                </Tab>

                <Tab eventKey="groups" title="Groups">
                  <div className="mb-3">
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Search by Name</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter group name..."
                            value={groupSearchName}
                            onChange={(e) => setGroupSearchName(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Country</Form.Label>
                          <Form.Select
                            value={groupSearchCountry}
                            onChange={(e) => handleGroupCountryChange(e.target.value)}
                          >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                              <option key={country.countryCode} value={country.countryCode}>
                                {country.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>State</Form.Label>
                          <Form.Select
                            value={groupSearchState}
                            onChange={(e) => setGroupSearchState(e.target.value)}
                            disabled={!groupSearchCountry}
                          >
                            <option value="">All States</option>
                            {states.map(state => (
                              <option key={state.stateCode} value={state.stateCode}>
                                {state.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Select Groups ({filteredGroups.length} found)</Form.Label>
                    <div className="row">
                      {filteredGroups.map(group => (
                        <div key={group._id} className="col-md-4 col-sm-6 mb-2">
                          <Form.Check
                            type="checkbox"
                            id={`group-${group._id}`}
                            label={`${group.name} (${group.state || 'N/A'})`}
                            checked={selectedGroups.includes(group._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedGroups(prev => [...prev, group._id]);
                              } else {
                                setSelectedGroups(prev => prev.filter(id => id !== group._id));
                              }
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {filteredGroups.length === 0 && groups.length > 0 && (
                      <p className="text-muted">No groups match your search criteria.</p>
                    )}
                    {groups.length === 0 && (
                      <p className="text-muted">No groups available. Please add groups first.</p>
                    )}
                  </Form.Group>
                </Tab>

                <Tab eventKey="members" title="Cast & Crew">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6>Cast & Crew Members</h6>
                    <Button variant="outline-primary" size="sm" onClick={addMember}>
                      <i className="bi bi-plus"></i> Add Member
                    </Button>
                  </div>
                  {members.map((member, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Body>
                        <Row>
                          <Col md={3}>
                            <Form.Group className="mb-2">
                              <Form.Label>First Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={member.firstName}
                                onChange={(e) => updateMember(index, 'firstName', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-2">
                              <Form.Label>Last Name</Form.Label>
                              <Form.Control
                                type="text"
                                value={member.lastName}
                                onChange={(e) => updateMember(index, 'lastName', e.target.value)}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-2">
                              <Form.Label>Role</Form.Label>
                              <Form.Select
                                value={member.memberTypeId}
                                onChange={(e) => updateMember(index, 'memberTypeId', e.target.value)}
                              >
                                <option value="">Select Role</option>
                                {memberTypes.map(type => (
                                  <option key={type._id} value={type._id}>
                                    {type.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={2}>
                            <Form.Label>&nbsp;</Form.Label>
                            <div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => removeMember(index)}
                              >
                                <i className="bi bi-trash"></i>
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))}
                  {memberTypes.length === 0 && (
                    <p className="text-muted">No member types available. Please add member types first.</p>
                  )}
                </Tab>
              </Tabs>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploading}>
                {uploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>{editingPlay ? "Update" : "Create"} Play</>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Schedule Modal */}
        <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Manage Schedules - {selectedPlayForSchedule?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs defaultActiveKey="add" className="mb-3">
              <Tab eventKey="add" title="Add Schedule">
                <Form onSubmit={handleScheduleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="startDate"
                          value={scheduleData.startDate}
                          onChange={handleScheduleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control
                          type="datetime-local"
                          name="endDate"
                          value={scheduleData.endDate}
                          onChange={handleScheduleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Timing</Form.Label>
                    <Form.Control
                      type="text"
                      name="timing"
                      value={scheduleData.timing}
                      onChange={handleScheduleInputChange}
                      placeholder="e.g., 7:00 PM - 9:30 PM"
                      required
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Country</Form.Label>
                        <Form.Select
                          name="country"
                          value={scheduleData.country}
                          onChange={handleScheduleInputChange}
                          required
                        >
                          <option value="">Select Country</option>
                          {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>
                              {country.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>State</Form.Label>
                        <Form.Select
                          name="state"
                          value={scheduleData.state}
                          onChange={handleScheduleInputChange}
                          required
                        >
                          <option value="">Select State</option>
                          {states.map(state => (
                            <option key={state.stateCode} value={state.stateCode}>
                              {state.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Pin Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="pinCode"
                          value={scheduleData.pinCode}
                          onChange={handleScheduleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={scheduleData.address}
                          onChange={handleScheduleInputChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Organizer Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="organizerName"
                          value={scheduleData.organizerName}
                          onChange={handleScheduleInputChange}
                          placeholder="Enter organizer name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Organizer Phone</Form.Label>
                        <Form.Control
                          type="tel"
                          name="organizerPhone"
                          value={scheduleData.organizerPhone}
                          onChange={handleScheduleInputChange}
                          placeholder="Enter organizer phone"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Button variant="primary" type="submit">
                    Add Schedule
                  </Button>
                </Form>
              </Tab>
              
              <Tab eventKey="list" title="Existing Schedules">
                <div className="schedule-list">
                  {schedules.length > 0 ? (
                    schedules.map((schedule, index) => (
                      <Card key={index} className="mb-2">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{new Date(schedule.startDate).toLocaleDateString()}</strong>
                              {schedule.endDate && (
                                <span> - {new Date(schedule.endDate).toLocaleDateString()}</span>
                              )}
                              <div className="small text-muted">{schedule.timing}</div>
                              <div className="small">{schedule.address}</div>
                              <div className="small text-muted">{schedule.pinCode}</div>
                            </div>
                            <div className="text-end">
                              <Badge bg="info">{schedule.state}</Badge>
                              <div className="mt-1">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteSchedule(schedule._id)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <p className="text-muted">No schedules found for this play.</p>
                  )}
                </div>
              </Tab>
              
              <Tab eventKey="tickets" title="Ticket Options">
                <div className="mb-4">
                  <h6 style={{ color: '#810081' }}>Configure Ticket Types for Specific Schedule</h6>
                  <small className="text-muted">Select a schedule to set up ticket options for that specific show</small>
                </div>
                
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Select Schedule</Form.Label>
                  <Form.Select
                    value={selectedScheduleId}
                    onChange={(e) => {
                      setSelectedScheduleId(e.target.value);
                      if (e.target.value) {
                        loadTicketOptionsForSchedule(e.target.value);
                      } else {
                        setTicketTypes([{ ticketName: '', price: 0 }]);
                      }
                    }}
                    style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                  >
                    <option value="">Choose a schedule...</option>
                    {schedules.map((schedule) => (
                      <option key={schedule._id} value={schedule._id}>
                        {new Date(schedule.startDate).toLocaleDateString()} - {schedule.timing} ({schedule.address})
                      </option>
                    ))}
                  </Form.Select>
                  {schedules.length === 0 && (
                    <Form.Text className="text-muted">
                      No schedules available. Please add a schedule first.
                    </Form.Text>
                  )}
                </Form.Group>
                
                {selectedScheduleId && ticketTypes.map((ticket, index) => (
                  <Card key={index} className="mb-3 border-2" style={{ borderColor: '#FFD600' }}>
                    <Card.Body>
                      <Row className="g-3 align-items-end">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                              Ticket Name
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="e.g., Gold, Silver, General"
                              value={ticket.ticketName}
                              onChange={(e) => updateTicketType(index, 'ticketName', e.target.value)}
                              style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label className="fw-semibold" style={{ color: '#810081' }}>
                              Price (₹)
                            </Form.Label>
                            <Form.Control
                              type="number"
                              placeholder="0"
                              min="1"
                              value={ticket.price || ''}
                              onChange={(e) => updateTicketType(index, 'price', parseInt(e.target.value) || 0)}
                              style={{ borderRadius: '10px', borderColor: '#FFD600' }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={2}>
                          <Button
                            variant="outline-danger"
                            onClick={() => removeTicketType(index)}
                            disabled={ticketTypes.length === 1}
                            className="w-100"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
                
                {selectedScheduleId && (
                  <div className="text-center mb-3">
                    <Button
                      variant="outline-secondary"
                      onClick={addTicketType}
                      className="rounded-pill me-2"
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Ticket Type
                    </Button>
                    <Button
                      onClick={saveTicketTypes}
                      disabled={loading || !selectedScheduleId}
                      style={{ backgroundColor: '#FFD600', color: '#810081', border: 'none' }}
                      className="rounded-pill"
                    >
                      {loading ? <Spinner size="sm" className="me-2" /> : <i className="bi bi-save me-2"></i>}
                      Save Ticket Options
                    </Button>
                  </div>
                )}
                
                {!selectedScheduleId && schedules.length > 0 && (
                  <div className="text-center py-4">
                    <i className="bi bi-ticket-perforated" style={{ fontSize: '3rem', color: '#810081', opacity: 0.3 }}></i>
                    <p className="text-muted mt-2">Please select a schedule to configure ticket options</p>
                  </div>
                )}
                
                {error && (
                  <Alert variant="danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success">
                    <i className="bi bi-check-circle me-2"></i>
                    {success}
                  </Alert>
                )}
              </Tab>
            </Tabs>
          </Modal.Body>
        </Modal>


      </div>
    </PermissionWrapper>
  );
};

export default ManageAllPlaysPage;